import { Images } from '../assets'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import PageTitle from '../components/PageTitle'
import ProjectCard from '../components/ProjectCard'


const placeholderImg = (label) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect width="600" height="400" fill="#E6F0FF"/><text x="300" y="200" text-anchor="middle" dominant-baseline="middle" fill="#7E7E7E" font-size="18" font-family="sans-serif">${label}</text></svg>`
  )}`

const services = {
  'digital-health-systems': {
    metaTitle: 'Digital Health Systems Design and Development | SeamHealth Innovations',
    heroTitle: 'Digital Health Systems',
    heroSub: 'We design and build the platforms that run health programmes - from initial scoping through to deployment and beyond.',
    heroIntro: 'Health programmes depend on reliable digital infrastructure. A patient registry that crashes at a busy facility, a data collection tool that fails without internet, or a laboratory system that cannot talk to existing government databases does not just cause technical problems - it causes harm. We build systems that work in the environments where they are actually used.',
    coversIntro: 'We build across the full range of digital health system types:',
    covers: [
      'Patient registries - centralized platforms for tracking patients, cases, and outcomes across facilities and geographies',
      'Laboratory information management systems - digital tracking of samples, workflows, and results from collection through to reporting',
      'Civil registration and vital statistics platforms - systems for recording births, deaths, and other vital events at scale',
      'Mobile data collection tools - offline-first applications for community health workers and field teams operating without reliable connectivity',
      'Vaccination tracking systems - platforms for monitoring post-vaccination patient data, adverse reactions, and coverage rates',
      'HIV and disease-specific programme platforms - data capture and case management tools supporting testing, treatment, and follow-up workflows',
    ],
    howWeWork: [
      'We start by understanding the programme - not the technology. Who are the end users? What decisions does this system need to support? What does the data flow look like from a facility to a state ministry to a donor? The answers to those questions shape the architecture before a line of code is written.',
      'We build with integration in mind. Our systems are designed to work alongside existing government databases and health information infrastructure, not to replace them. We support interoperability standards and build with the assumption that the system will need to share data with other platforms over its lifetime.',
      'We build for the field. Offline-first functionality, low-bandwidth optimisation, and interfaces designed for health workers using devices in busy, low-resource settings are not optional extras - they are baseline requirements in the environments where our systems are deployed.',
    ],
    platform: null,
    caseStudiesIntro: 'See how we have delivered digital health systems for clients across Nigeria and Sub-Saharan Africa.',
    caseStudies: [
      { title: 'Cleft and Surgery E-Registry', client: 'SmileTrain Inc. and Federal Ministry of Health Nigeria', image: Images.SmiletrainGallery3, link: '/projects/cleft-surgery', bgColor: 'blue' },
      { title: 'OpenCRVS - Civil Registration', client: 'UNICEF - National Population Council (NPC)', image: placeholderImg('OpenCRVS'), link: '/projects/opencrvs', bgColor: 'blue' },
      { title: 'Ekiti LIMS and Vaccination Tracker', client: 'Ekiti State Ministry of Health and Human Services', image: Images.EkitiKeyFeatures, link: '/projects/ekiti', bgColor: 'blue' },
    ],
    ctaHeading: 'Building something new?',
    ctaBody: 'Tell us about your programme and the system you need. We will tell you how we can help.',
  },
  'health-data-analytics': {
    metaTitle: 'Health Data Collection and Analytics Services | SeamHealth Innovations',
    heroTitle: 'Health Data and Analytics',
    heroSub: 'We deploy data collection platforms at scale and turn raw responses into the evidence that drives programme decisions.',
    heroIntro: 'Global health programmes run on data. Enrollment gaps cannot be addressed without knowing where they are. Retention cannot be improved without tracking who is dropping out. Quality cannot be improved without measuring what patients actually experience. We build and deploy the data infrastructure that makes those insights possible - at the scale and speed that programme managers and donors need.',
    coversIntro: 'Our data and analytics work covers:',
    covers: [
      'Multi-site data collection deployments - configuring and deploying data collection platforms across dozens or hundreds of health facilities simultaneously',
      'Beneficiary surveys and patient feedback systems - structured data collection from programme beneficiaries on awareness, enrollment, satisfaction, and experience',
      'Real-time analytics dashboards - configuring dashboard access for programme managers at the facility, state, and national level',
      'Continuous quality improvement programmes - deploying feedback systems that generate actionable insights for healthcare providers to improve service quality',
      'Programme evidence and reporting - generating structured reports for programme management, donor reporting, and evidence-based decision-making',
      'Large-scale data deployments - managing sustained, high-volume data collection over extended programme periods',
    ],
    howWeWork: [],
    platform: {
      heading: 'Our Platform: Clientshot',
      paragraphs: [
        'Clientshot is our patient feedback and data collection platform, built for high-volume, multi-site deployments in low-resource health settings. It supports data collection via web app, mobile app, USSD, and WhatsApp - reaching beneficiaries regardless of device type or connectivity.',
        'Clientshot has been deployed across 224 health facilities in Nigeria, collecting over 120,000 beneficiary responses for IHVN, APIN, and the Global Fund N-THRIP programme. Clientshot was presented at INTEREST 2025 in Windhoek, Namibia, where our team shared peer-reviewed evidence on its use in HIV care quality improvement programmes.',
        'We configure, deploy, and manage Clientshot for client programmes. We also build bespoke data collection systems where a client\'s requirements go beyond what Clientshot covers.',
      ],
    },
    caseStudiesIntro: 'See how we have deployed data collection and analytics at scale for health programmes across Nigeria.',
    caseStudies: [
      { title: 'Health Insurance Assessment - FCT', client: 'IHVN', image: Images.ClientshotProjectCard, link: '/projects/clientshot-fct', bgColor: 'purple' },
      { title: 'KNCV TB Patient Retention System', client: 'KNCV Tuberculosis Foundation Nigeria', image: Images.KncvGallery3, link: '/projects/kncv', bgColor: 'blue' },
      { title: 'SMARTest - HIV Testing Services', client: 'IHVN', image: placeholderImg('SMARTest'), link: '/projects/smartest', bgColor: 'blue' },
    ],
    ctaHeading: 'Need data to drive your programme?',
    ctaBody: 'Tell us what decisions you need to make and we will show you how we can get you the data to make them.',
  },
  'implementation-advisory': {
    metaTitle: 'Health IT Implementation and Advisory Services | SeamHealth Innovations',
    heroTitle: 'Implementation and Advisory',
    heroSub: 'We assess, advise, and support - staying engaged long after a system goes live.',
    heroIntro: 'A digital health system is not finished when it is built. The hardest part of any technology deployment is not the software - it is adoption. Health workers need to trust the system and know how to use it. Programme managers need confidence in the data it produces. Government stakeholders need assurance that the platform fits within existing infrastructure. We cover all of that.',
    coversIntro: 'Our implementation and advisory services include:',
    covers: [
      'System evaluation and gap analysis - structured, field-based assessments of existing digital health platforms against real-world operational requirements',
      'EHR and platform assessments - evaluating electronic health record systems and other health IT platforms for fitness of purpose, interoperability, and adoption barriers',
      'Implementation support - hands-on deployment coordination, facility onboarding, and technical setup for new system rollouts',
      'Staff training and capacity building - training health workers, facility managers, and programme staff on system use and data management',
      'Legacy system modernization - assessing and upgrading existing systems to meet current technical standards and programme requirements',
      'Ongoing technical support and maintenance - post-deployment support to ensure systems remain operational and continue to meet programme needs',
    ],
    howWeWork: [
      'Our advisory work is grounded in field evidence, not desk reviews. When we evaluate a system, we visit facilities, speak with the health workers using it, and observe how workflows actually operate - not how they are supposed to operate on paper. That distinction matters. The gap between a system\'s design and its real-world use is where most implementation failures begin.',
      'When we support implementation, we treat go-live as a milestone, not a finish line. We remain engaged through the post-deployment period, monitoring adoption, resolving issues as they emerge, and adapting to the realities of the environment.',
      'Our advisory engagements produce structured, evidence-based outputs - gap analysis reports, evaluation findings, and recommendations that clients can act on and reference for future programme decisions.',
    ],
    platform: null,
    caseStudiesIntro: 'See how we have supported implementation and provided advisory services for health programmes and government clients.',
    caseStudies: [
      { title: 'Talamus EHR Evaluation - Lagos State', client: 'CHAI - Lagos State Primary Health Care Board', image: Images.TalamusProject, link: '/projects/talamus', bgColor: 'blue' },
      { title: 'KNCV TB Patient Retention System', client: 'KNCV Tuberculosis Foundation Nigeria', image: Images.KncvGallery3, link: '/projects/kncv', bgColor: 'blue' },
      { title: 'SMARTest - HIV Testing Services', client: 'IHVN', image: placeholderImg('SMARTest'), link: '/projects/smartest', bgColor: 'blue' },
    ],
    ctaHeading: 'Need a second opinion on your current system?',
    ctaBody: 'Whether you are evaluating an existing platform, planning a new deployment, or looking for sustained technical support, we can help.',
  },
}

const SectionHeading = ({ children, className = '' }) => (
  <h1 className={`text-5xl font-bold text-[#2A2A2A] tracking-tight ${className}`}>{children}</h1>
)

const Servicedetail = () => {
  const location = useLocation()
  const slug = location.pathname.split('/').pop()
  const service = services[slug]

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  if (!service) {
    return (
      <>
        <PageTitle title="Service Not Found | SeamHealth Innovations" />
        <section className="min-h-screen flex items-center justify-center bg-[#F8F9FB]">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-[#2A2A2A]">Service not found</h2>
            <Link to="/services" className="btn btn-primary mt-6">See all services</Link>
          </div>
        </section>
      </>
    )
  }

  return (
    <>
      <PageTitle title={service.metaTitle} />

      {/* Hero */}
      <section className="project-section relative flex flex-col justify-center !h-auto min-h-[78vh] md:min-h-[84vh] lg:min-h-[90vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover -z-[1000]"
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          disablePictureInPicture
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12 pt-5 pb-16 md:pt-5 md:pb-20 lg:pt-5 lg:pb-24">
          <div className="max-w-[760px]">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-white/70">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">Home</Link>
                </li>
                <li className="text-white/40">/</li>
                <li>
                  <Link to="/services" className="hover:text-white transition-colors">Services</Link>
                </li>
                <li className="text-white/40">/</li>
                <li className="text-white" aria-current="page">{service.heroTitle}</li>
              </ol>
            </nav>
            <h1 data-aos="fade-up" className="text-5xl font-bold text-white mb-6">{service.heroTitle}</h1>
            <p data-aos="fade-up" className="text-xl md:text-2xl text-white/80 leading-relaxed mb-8">
              {service.heroSub}
            </p>
            <p data-aos="fade-up" className="text-white/60 leading-relaxed">
              {service.heroIntro}
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 h-32 md:h-44">
          <svg
            viewBox="0 0 1440 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full block"
          >
            <path d="M0 200H1440V0L0 200Z" fill="white" />
          </svg>
        </div>

      </section>

      {/* What This Service Covers */}
      <section className="py-40 md:py-48 bg-white">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-up">
              <SectionHeading>What This Service Covers</SectionHeading>
              <img decoding="async" src={slug === 'digital-health-systems' ? Images.DigitalHealthCover : slug === 'health-data-analytics' ? Images.Image19 : slug === 'implementation-advisory' ? Images.AdvisoryCover : Images.ServiceCover3} alt="What This Service Covers" className="w-full aspect-[4/3] mt-8 rounded-2xl object-cover transition-transform duration-500 ease-out hover:scale-105" loading="lazy" />
            </div>
            <div data-aos="fade-up" data-aos-delay={100}>
              <p className="text-lg text-[#5B5B5B] leading-relaxed mb-8">{service.coversIntro}</p>
              <ul className="space-y-5">
                {service.covers.map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="shrink-0 w-2 h-2 rounded-full bg-[#007DD6] mt-2" />
                    <span className="text-[#5B5B5B] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work / Our Platform */}
      <section className="py-40 md:py-48 bg-[#F8F9FB]">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          {service.platform ? (
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div data-aos="fade-up">
                <SectionHeading>{service.platform.heading}</SectionHeading>
                <img decoding="async" src={Images.Image18} alt={service.platform.heading} className="w-full aspect-[4/3] mt-8 rounded-2xl object-cover transition-transform duration-500 ease-out hover:scale-105" loading="lazy" />
              </div>
              <div data-aos="fade-up" data-aos-delay={100} className="space-y-6 text-[#5B5B5B] leading-relaxed">
                {service.platform.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div data-aos="fade-up">
                <SectionHeading>How We Work</SectionHeading>
                <img decoding="async" src={slug === 'digital-health-systems' ? Images.ServiceImg3520 : slug === 'implementation-advisory' ? Images.AdvisoryWork : Images.EhrImg3116} alt="How We Work" className="w-full aspect-[4/3] mt-8 rounded-2xl object-cover transition-transform duration-500 ease-out hover:scale-105" loading="lazy" />
              </div>
              <div data-aos="fade-up" data-aos-delay={100} className="space-y-6 text-[#5B5B5B] leading-relaxed">
                {service.howWeWork.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* This Service in Action */}
      <section className="py-40 md:py-48 bg-white">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="max-w-[640px] mb-16">
            <SectionHeading>This Service in Action</SectionHeading>
            <p className="text-lg text-[#5B5B5B] leading-relaxed mt-4">{service.caseStudiesIntro}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {service.caseStudies
              .filter((study) => study.image && !String(study.image).startsWith('data:image/svg+xml'))
              .map((study, i) => (
                <ProjectCard
                  key={i}
                  image={study.image}
                  title={study.title}
                  client={study.client}
                  link={study.link}
                  bgColor={study.bgColor}
                  ctaLabel="View case study"
                  tag={false}
                  published
                  compactImage
                />
              ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-40 md:py-48 bg-gradient-to-br from-[#001630] to-[#002A4D] overflow-hidden">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-[1] opacity-[0.2]">
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[2] bg-gradient-to-br from-[#001630]/60 to-[#002A4D]/60" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
              <h1 data-aos="fade-up" className="text-5xl text-white tracking-tight">
                {service.ctaHeading}
              </h1>
              <p data-aos="fade-up" className="text-lg text-white/70 mt-4 leading-relaxed max-w-[480px]">
                {service.ctaBody}
              </p>
              <div data-aos="fade-up" className="mt-8 flex flex-wrap gap-4">
                <Link to="/get-in-touch" className="btn btn-primary text-base w-fit">
                  Get in touch <img decoding="async" src={Images.ArrowUpRight} alt="" loading="lazy" />
                </Link>
                <Link to="/services" className="btn btn-secondary text-white">
                  See all our services
                </Link>
              </div>
        </div>
      </section>
    </>
  )
}

export default Servicedetail
